import React, { useEffect, useState } from 'react';
import API from '../../api';
import MessageModal from '../smallComponent/MessageModal';
import imageCompression from 'browser-image-compression';
import { API_IMAGE_URL} from '../../lib/api'
import { addActivity } from '../../utils/activityService';

export default function Attendance({ employeeId: propEmployeeId }) {
  const [attendance, setAttendance] = useState(null);
  const [entryPhoto, setEntryPhoto] = useState(null);
  const [exitPhoto, setExitPhoto] = useState(null);
  const [message, setMessage] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  const [modalShow, setModalShow] = useState(false);
  const [modalType, setModalType] = useState('info');

  const employee = JSON.parse(localStorage.getItem('employee'));
  const employeeId = propEmployeeId || employee?.id;

  // Live time clock
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch today's attendance on load or employeeId change
  useEffect(() => {
    if (employeeId) fetchTodayAttendance();
  }, [employeeId]);

  const fetchTodayAttendance = async () => {
    try {
      const res = await API.get(`/attendance/today/${employeeId}`);
      setAttendance(res.data || null);
      // Clear local previews if backend photo exists to avoid stale previews
      if (res.data?.entry_photo) setEntryPhoto(null);
      if (res.data?.exit_photo) setExitPhoto(null);
    } catch (err) {
      console.error('Fetch attendance error:', err);
    }
  };

const handleEntrySubmit = async (photo) => {
  if (!photo) {
    showModal('Upload entry photo', 'warning');
    return;
  }

  const formData = new FormData();
  formData.append('entry_photo', photo);
  formData.append('employee_id', employeeId);

  try {
    const res = await API.post('/attendance/entry', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    // ✅ Success message
    showModal(res.data.message, 'success');

    if (res.data.attendance) {
      setAttendance(res.data.attendance);
      setEntryPhoto(null); // clear preview

      const attendanceData = res.data.attendance;
      const entryTime = attendanceData.entry_time;

      // ✅ Late arrival tracking
      if (!entryTime || entryTime > '11:00:00') {
        await addActivity(employeeId, `Late Arrival at ${entryTime}`, 'attendance');
      }
    } else {
      fetchTodayAttendance();
    }
  } catch (err) {
    console.error('❌ Entry Error:', err.response?.data || err.message);

    // ✅ Show backend error message if available
    const errorMsg = err.response?.data?.message || 'Something went wrong. Try again.';
    showModal(errorMsg, 'error');
  }
};


  const handleExitSubmit = async (photo) => {
    if (!photo) {
      showModal('Upload exit photo', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('photo', photo);

    try {
      const res = await API.post(`/attendance/exit/${attendance.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      showModal(res.data.message, 'success');

      if (res.data.attendance) {
        setAttendance(res.data.attendance);
        setExitPhoto(null);
        const attendanceData = res.data.attendance;
       const exitTime = attendanceData.exit_time;
      if (!exitTime || exitTime < '17:30:00') {
        await addActivity(employeeId, `Early Exit at ${exitTime}`, 'attendance');
      }
      } else {
        fetchTodayAttendance();
      }
    } catch (err) {
      console.error('❌ Exit Error:', err.response?.data || err.message);
      showModal('Exit marking failed.', 'error');
    }
  };

  const compressAndSet = async (file, type = 'entry') => {
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.2,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      });

      if (type === 'entry') {
        setEntryPhoto(compressed); // preview compressed image
        await handleEntrySubmit(compressed);
      } else {
        setExitPhoto(compressed);
        await handleExitSubmit(compressed);
      }
    } catch (err) {
      console.error('❌ Compression error:', err);
      showModal('Image compression failed.', 'error');
    }
  };

  const showModal = (msg, type = 'info') => {
    setMessage(msg);
    setModalType(type);
    setModalShow(true);
  };

  return (
    <div className="container mx-auto  px-1">
      <h4 className="text-2xl font-semibold mb-6">📆 Mark Attendance</h4>

      <div className="bg-blue-100 border border-blue-300 rounded-md p-4 flex justify-between mb-6">
        <div>
          <p><strong>👤 {employee?.full_name || 'N/A'} </strong></p>
          <p><strong>📞 {employee?.phone || 'N/A'}</strong> </p>
        </div>
        <div className="text-right">
          <p><strong>🕒 Date:</strong> {currentTime.toLocaleDateString()}</p>
          <p><strong>⏰ Time:</strong> {currentTime.toLocaleTimeString()}</p>
        </div>
      </div>
        <div className="bg-blue-100 border border-blue-300 rounded-md p-4 mb-6">
           <p ><strong>Worked:</strong> {attendance?.working_hours || ''}</p>
        </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Entry Section */}
        <div className="border border-green-500 rounded-lg shadow-md">
          <div className="bg-green-600 text-white font-medium px-4 py-2 rounded-t-lg">
            Entry
          </div>
          <div className="p-4">
            {attendance?.entry_time ? (
              <>
                <p className="mb-2"><strong>Time:</strong> {attendance.entry_time}</p>
                <img
                  src={
                    entryPhoto
                      ? URL.createObjectURL(entryPhoto)
                      : `${API_IMAGE_URL}${attendance.entry_photo}`
                  }
                  alt="Entry"
                  className="rounded-md object-cover w-full max-h-64"
                />
              </>
            ) : (
              <>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) compressAndSet(file, 'entry');
                  }}
                  className="mb-3 block w-full rounded border border-gray-300 p-2 text-gray-700"
                  required
                />
                {entryPhoto && (
                  <img
                    src={URL.createObjectURL(entryPhoto)}
                    alt="Entry Preview"
                    className="rounded-md object-cover w-full max-h-52"
                  />
                )}
              </>
            )}
          </div>
        </div>

        {/* Exit Section */}
        <div className="border border-red-500 rounded-lg shadow-md">
          <div className="bg-red-600 text-white font-medium px-4 py-2 rounded-t-lg">
            Exit
          </div>
          <div className="p-4">
            {attendance?.exit_time ? (
              <>
                <p className="mb-2"><strong>Time:</strong> {attendance.exit_time} </p>
                
                <img
                  src={
                    exitPhoto
                      ? URL.createObjectURL(exitPhoto)
                      : `${API_IMAGE_URL}${attendance.exit_photo}`
                  }
                  alt="Exit"
                  className="rounded-md object-cover w-full max-h-64"
                />
              </>
            ) : attendance?.entry_time ? (
              <>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) compressAndSet(file, 'exit');
                  }}
                  className="mb-3 block w-full rounded border border-gray-300 p-2 text-gray-700"
                  required
                />
                {exitPhoto && (
                  <img
                    src={URL.createObjectURL(exitPhoto)}
                    alt="Exit Preview"
                    className="rounded-md object-cover w-full max-h-52"
                  />
                )}
              </>
            ) : (
              <p className="text-gray-500 italic">Please mark entry first.</p>
            )}
          </div>
        </div>
      </div>

      {/* Modal Message */}
      <MessageModal
        show={modalShow}
        onClose={() => setModalShow(false)}
        title="Attendance Update"
        message={message}
        type={modalType}
      />
    </div>
  );
}
